import { ChevronDown, ChevronRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { ISideBarItem } from '../../../resources/interfaces';
import useUserAccount from '../../../hooks/userAccountHook';
import NavigationItem from './NavigationItem';

interface NavigationSectionProps {
  categoryKey: string;
  title: string;
  navItems: ISideBarItem[];
  isMobile?: boolean;
  hideSectionHeader?: boolean;
  isSidebarExpanded: boolean;
  onMenuClick: () => void;
  expandedCategories: Record<string, boolean>;
  toggleCategory: (category: string) => void;
  expandedNavItems: Set<string>;
  toggleNavItem: (path: string) => void;
  handleToggleFullNav?: () => void;
}

export default function NavigationSection({
  categoryKey,
  title,
  navItems,
  isMobile = false,
  hideSectionHeader = false,
  isSidebarExpanded,
  onMenuClick,
  expandedCategories,
  toggleCategory,
  expandedNavItems,
  toggleNavItem,
  handleToggleFullNav
}: NavigationSectionProps) {
  const { user } = useUserAccount();

  const userHasAccess = (sideBarItem: ISideBarItem): boolean => {
    const userAccess = user.accessRight;
    const teamType = user.company?.type;

    if (!teamType || !userAccess) {
      const showItem = (!sideBarItem.teamTypes?.length) && (!sideBarItem.access?.length);
      return showItem;
    }

    const teamTypeCheck = !sideBarItem.teamTypes?.length || sideBarItem.teamTypes.includes(teamType);
    const accessCheck = !sideBarItem.access?.length || sideBarItem.access.includes(userAccess);

    return teamTypeCheck && accessCheck;
  };

  const filteredItems = navItems.filter(nav => userHasAccess(nav));

  if (filteredItems.length === 0) return null;

  // For collapsed sidebar, only show navigation items without section title
  if (!isSidebarExpanded && !isMobile) {
    return (
      <div className="px-2">
        {filteredItems.map((nav, index) => (
          <NavigationItem
            key={index}
            nav={nav}
            index={index}
            isMobile={isMobile}
            isSidebarExpanded={isSidebarExpanded}
            onMenuClick={onMenuClick}
            expandedNavItems={expandedNavItems}
            toggleNavItem={toggleNavItem}
          />
        ))}
      </div>
    );
  }

  // For broker with hidden section headers, render items directly
  if (hideSectionHeader) {
    return (
      <div className={isMobile ? '' : 'px-4'}>
        <div className="space-y-1">
          {filteredItems.map((nav, index) => (
            <NavigationItem
              key={index}
              nav={nav}
              index={index}
              isMobile={isMobile}
              isSidebarExpanded={isSidebarExpanded}
              onMenuClick={onMenuClick}
              expandedNavItems={expandedNavItems}
              toggleNavItem={toggleNavItem}
              handleToggleFullNav={handleToggleFullNav}
            />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={isMobile ? '' : 'px-4'}>
      <button
        onClick={() => toggleCategory(categoryKey)}
        className="w-full flex items-center justify-between py-2 px-1 text-sm font-semibold text-gray-900 dark:text-darkBg-mainText rounded-md hover:bg-gray-50 dark:hover:bg-gray-800"
      >
        <span>{title}</span>
        {expandedCategories[categoryKey] ? (
          <ChevronDown className="h-4 w-4" />
        ) : (
          <ChevronRight className="h-4 w-4" />
        )}
      </button>
      <motion.div
        initial={{ opacity: 0, y: 0 }}
        animate={expandedCategories[categoryKey] ? { opacity: 1, y: 0 } : { opacity: 0, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <div className={`mt-1 space-y-1 border-l-2 border-gray-100 dark:border-darkBg-border pl-2 ${expandedCategories[categoryKey] ? '' : 'hidden'}`}>
          {filteredItems.map((nav, index) => (
            <NavigationItem
              key={index}
              nav={nav}
              index={index}
              isMobile={isMobile}
              isSidebarExpanded={isSidebarExpanded}
              onMenuClick={onMenuClick}
              expandedNavItems={expandedNavItems}
              toggleNavItem={toggleNavItem}
              handleToggleFullNav={handleToggleFullNav}
            />
          ))}
        </div>
      </motion.div>
    </div>
  );
}
